import {Action, Button, Card, DataStatus, openDialog} from 'platform/components';
import {Box, HStack, Right, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetRentalOrderQuery, useReserveRentalOrderVehicleMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {handleApiError, Main} from '@dms/shared';
import {RentalVehicleBlockConflictDialog} from '@dms/vehicle-detail';

import {ApiError, buildArray, composePath, TestIdProps, useBoolean, useNavigate} from 'shared/web';

import {CustomerCard} from './components/CustomerCard';
import {RentalVehicleDetail} from './components/RentalVehicleDetail';
import {RentalVehicleSelect} from './components/RentalVehicleSelect';
import {getVehicleBlockedErrorMessage} from './utils/getVehicleBlockedErrorMessage';
import {isVehicleChangeBlocked} from './utils/isVehicleChangeBlocked';

interface VehicleAndCustomerProps extends TestIdProps {
  rentalOrderId: string | undefined;
  setRentalOrderId: (rentalOrderId: string | undefined) => void;
}

export function VehicleAndCustomer(props: VehicleAndCustomerProps) {
  const navigate = useNavigate();

  const {
    data: rentalOrder,
    isLoading: isLoadingRentalOrder,
    isError: isErrorRentalOrder,
  } = useGetRentalOrderQuery(
    {rentalOrderId: props.rentalOrderId ?? ''},
    {skip: !props.rentalOrderId}
  );

  const [reserveRentalVehicle, {isLoading: isReservingRentalVehicle}] =
    useReserveRentalOrderVehicleMutation();

  const [isVehicleEditModeRequested, openVehicleEditMode, closeVehicleEditMode] = useBoolean(
    isNil(props.rentalOrderId)
  );

  const rentalOrderState = rentalOrder?.state;
  const isVehicleChangeDisabled = isVehicleChangeBlocked(rentalOrderState);

  // The API rejects a vehicle change in a blocked state, so an already opened form is closed too.
  const isVehicleEditModeOpened = isVehicleEditModeRequested && !isVehicleChangeDisabled;

  // Exhaustive over the blocking states, so a new one cannot be disabled without a reason.
  const vehicleChangeDisabledTooltip = isVehicleChangeDisabled
    ? match(rentalOrderState)
        .with('rented', () => i18n.t('page.rentalOrder.labels.cannotChangeVehicleRentedTooltip'))
        .with('canceled', () =>
          i18n.t('page.rentalOrder.labels.cannotChangeVehicleCanceledTooltip')
        )
        .exhaustive()
    : undefined;

  const handleReserveError = (error: ApiError) => {
    const vehicleBlockedMessage = getVehicleBlockedErrorMessage(error);

    if (isNotNil(vehicleBlockedMessage)) {
      openDialog(
        <RentalVehicleBlockConflictDialog
          title={i18n.t('page.rentalOrder.labels.vehicleBlockedTitle')}
          description={i18n.t('page.rentalOrder.labels.vehicleBlockedDescription')}
          message={vehicleBlockedMessage}
        />,
        {
          'data-testid': testIds.rentalOrder.vehicleAndCustomer('vehicleBlocked'),
        }
      );
      return;
    }

    handleApiError(error);
  };

  // The rent step is closed for a draft order. A reservation refused earlier (for example
  // by a vehicle block) leaves the order in draft, so Continue retries it and explains a
  // refusal here instead of opening a section the user cannot enter.
  const handleReserve = async () => {
    const rentalOrderId = props.rentalOrderId;

    if (isNil(rentalOrderId)) {
      throw new Error('Rental order ID is required');
    }

    const openRentVehicle = () =>
      navigate(composePath(rentalOrderRoutes.rentVehicle, {params: {id: rentalOrderId}}));

    if (rentalOrderState !== 'draft') {
      openRentVehicle();
      return;
    }

    await reserveRentalVehicle({rentalOrderId})
      .unwrap()
      .then(openRentVehicle)
      .catch(handleReserveError);
  };

  const vehicleCardActions: Action[] = buildArray<Action>()
    .when(isNotNil(rentalOrder?.rentalVehicleId), {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('entity.vehicle.labels.vehicleDetails'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('vehicleDetails'),
      to: rentalOrder?.rentalVehicleId
        ? composePath(vehiclesRoutes.detail, {params: {id: rentalOrder.rentalVehicleId}})
        : undefined,
      target: '_blank',
    })
    .when(isNotNil(rentalOrder?.rentalVehicleId) && !isVehicleEditModeOpened, {
      type: 'button',
      variant: 'link',
      leftIcon: 'navigation/refresh',
      title: i18n.t('page.rentalOrder.labels.changeVehicleOrDate'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('changeVehicleOrDate'),
      isDisabled: isVehicleChangeDisabled,
      tooltip: vehicleChangeDisabledTooltip,
      onClick: openVehicleEditMode,
    })
    .when(isNotNil(rentalOrder?.rentalVehicleId) && isVehicleEditModeOpened, {
      type: 'button',
      variant: 'dangerLink',
      leftIcon: 'navigation/cancel',
      title: i18n.t('general.actions.discardChanges'),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('discardChanges'),
      onClick: closeVehicleEditMode,
    });

  // A blocked state wins over every other branch, so the form that submits the change
  // cannot render even when a vehicle was picked before the state changed.
  const rentalVehicleComponent = match([
    isVehicleChangeDisabled,
    isNotNil(rentalOrder?.rentalVehicleId),
    isVehicleEditModeOpened,
    isNotNil(props.rentalOrderId),
  ])
    .with([true, Pattern.any, Pattern.any, Pattern.any], () => (
      <RentalVehicleDetail rentalOrder={rentalOrder} />
    ))
    .with([false, Pattern.any, Pattern.any, false], () => (
      <RentalVehicleSelect onAfterSubmit={(id) => props.setRentalOrderId(id)} />
    ))
    .with([false, false, Pattern.any, true], () => (
      <RentalVehicleSelect rentalOrder={rentalOrder} onAfterSubmit={closeVehicleEditMode} />
    ))
    .with([false, true, true, true], () => (
      <RentalVehicleSelect rentalOrder={rentalOrder} onAfterSubmit={closeVehicleEditMode} />
    ))
    .otherwise(() => <RentalVehicleDetail rentalOrder={rentalOrder} />);

  return (
    <Main isFullHeight>
      <VStack spacing={4}>
        <HStack spacing={4}>
          <Box flex={1}>
            <Card
              title={i18n.t('page.rentalOrder.labels.rentalVehicle')}
              data-testid={testIds.rentalOrder.vehicleAndCustomer('rentalVehicle')}
              actions={vehicleCardActions}
            >
              <DataStatus
                isLoading={isLoadingRentalOrder}
                isError={isErrorRentalOrder}
                minHeight={isNil(rentalOrder?.rentalVehicleId) ? 61 : 160}
              >
                {rentalVehicleComponent}
              </DataStatus>
            </Card>
          </Box>

          <Box flex={1}>
            <CustomerCard
              rentalOrder={rentalOrder}
              setRentalOrderId={props.setRentalOrderId}
              data-testid={testIds.rentalOrder.vehicleAndCustomer('customerCard')}
            />
          </Box>
        </HStack>
        <Right>
          <Button
            variant="outlined"
            title={i18n.t('general.actions.continue')}
            onClick={handleReserve}
            isLoading={isReservingRentalVehicle}
            isDisabled={isNil(rentalOrder?.rentalVehicleId) || isNil(rentalOrder?.customerId)}
          />
        </Right>
      </VStack>
    </Main>
  );
}
